import { useState, useImperativeHandle, forwardRef } from 'react';
import { useSupplierCostDocuments } from '../hooks/useSupplierCostDocuments';
import { deleteSupplierCostDocument } from '../services/supplierCostDocumentService';
import { SupplierCostDocumentGrid } from './SupplierCostDocumentGrid';
import { SupplierCostDocumentLightbox } from './SupplierCostDocumentLightbox';
import type { SupplierCostDocumentView } from '../types';

export interface SupplierCostDocumentsSectionRef {
  refetch: () => Promise<void>;
}

interface SupplierCostDocumentsSectionProps {
  projectId: string;
  onRefetchReady?: (refetch: () => Promise<void>) => void;
}

export const SupplierCostDocumentsSection = forwardRef<
  SupplierCostDocumentsSectionRef,
  SupplierCostDocumentsSectionProps
>(function SupplierCostDocumentsSection({ projectId, onRefetchReady }, ref) {
  const { documents, isLoading, error, refetch } = useSupplierCostDocuments({
    projectId,
  });

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Expose refetch method via ref
  useImperativeHandle(ref, () => ({
    refetch,
  }));

  // Also notify parent of refetch function
  if (onRefetchReady) {
    onRefetchReady(refetch);
  }

  const selectedDocument = selectedIndex !== null
    ? documents[selectedIndex]
    : null;

  const handleDocumentClick = (document: SupplierCostDocumentView) => {
    const index = documents.findIndex((d) => d.id === document.id);
    setSelectedIndex(index);
  };

  const handleCloseLightbox = () => {
    setSelectedIndex(null);
  };

  const handlePrevious = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedIndex !== null && selectedIndex < documents.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handleDelete = async (document: SupplierCostDocumentView) => {
    setIsDeleting(true);
    try {
      await deleteSupplierCostDocument(document.id, document.filePath);
      await refetch();
    } catch (err) {
      console.error('Error deleting document:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdate = async (_document: SupplierCostDocumentView) => {
    // Refetch to get updated data including supplier name
    await refetch();
  };

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        Error loading supplier cost documents: {error}
      </div>
    );
  }

  return (
    <>
      <SupplierCostDocumentGrid
        documents={documents}
        onDocumentClick={handleDocumentClick}
        onDelete={handleDelete}
        showDeleteButton
        isLoading={isLoading}
        emptyMessage="No supplier cost documents yet"
      />

      {/* Lightbox */}
      {selectedDocument && (
        <SupplierCostDocumentLightbox
          document={selectedDocument}
          onClose={handleCloseLightbox}
          onPrevious={handlePrevious}
          onNext={handleNext}
          hasPrevious={selectedIndex !== null && selectedIndex > 0}
          hasNext={selectedIndex !== null && selectedIndex < documents.length - 1}
          onDelete={handleDelete}
          onUpdate={handleUpdate}
          showDeleteButton={!isDeleting}
        />
      )}
    </>
  );
});
